import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { gatewayIdForVerseEnv, getVersesByEnv } from '@rb/shared/admin-verses';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { readAuthToken } from '@/auth/auth-token';
import { fetchAllSales, type VxSale } from '@/client/vxshop.client';
import { adminQueryCount } from '@/client/admin.client';
import {
  getKpiActiveUsers,
  type KpiActiveUsers,
  getKpiRetention,
  type KpiRetention,
} from '@/client/kpi.client';
import { createdSinceFilter, buildKpiSignup, type KpiSignupView } from '@/lib/admin-aggregations';
import { SupabaseLoginGate } from '@/master-data/SupabaseLoginGate';
import { AttendanceDistributionPanel } from '@/components/admin/AttendanceDistributionPanel';

type EnvChoice = 'prod' | 'staging';

const VERSES_BY_ENV: Record<EnvChoice, readonly { verseId: string; alias: string }[]> = {
  prod: getVersesByEnv('prod'),
  staging: getVersesByEnv('staging'),
};

const usd = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** 로컬 자정 ms — daysAgo=0 = 오늘 자정, daysAgo=6 = 6일 전 자정(오늘 포함 7일 윈도우 시작). */
function localMidnightMs(daysAgo: number): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysAgo);
  return d.getTime();
}

export default function DashboardPage() {
  const [env, setEnv] = useState<EnvChoice>('prod');
  const [signup, setSignup] = useState<KpiSignupView | null>(null);
  const [signupErr, setSignupErr] = useState('');
  const [active, setActive] = useState<KpiActiveUsers | null>(null);
  const [activeErr, setActiveErr] = useState('');
  const [retention, setRetention] = useState<KpiRetention | null>(null);
  const [retentionErr, setRetentionErr] = useState('');
  const [sales, setSales] = useState<VxSale[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesProgress, setSalesProgress] = useState<{ page: number; total: number } | null>(null);
  const [salesErr, setSalesErr] = useState('');

  useEffect(() => {
    const todayMs = localMidnightMs(0);
    const weekMs = localMidnightMs(6);
    const monthMs = localMidnightMs(29);
    Promise.all([
      adminQueryCount({ collection: 'users', filters: [createdSinceFilter(todayMs)] }),
      adminQueryCount({ collection: 'users', filters: [createdSinceFilter(weekMs)] }),
      adminQueryCount({ collection: 'users', filters: [createdSinceFilter(monthMs)] }),
    ])
      .then(([today, thisWeek, thisMonth]) =>
        setSignup(buildKpiSignup({ today, thisWeek, thisMonth }))
      )
      .catch((e) => setSignupErr(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    getKpiActiveUsers()
      .then(setActive)
      .catch((e) => setActiveErr(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    getKpiRetention()
      .then(setRetention)
      .catch((e) => setRetentionErr(e instanceof Error ? e.message : String(e)));
  }, []);

  const handleLoadSales = async () => {
    const token = readAuthToken();
    if (!token) {
      setSalesErr('로그인 토큰이 없습니다. 다시 로그인하세요.');
      return;
    }
    setSalesLoading(true);
    setSalesErr('');
    setSales([]);
    setSalesProgress(null);
    try {
      const gatewayId = gatewayIdForVerseEnv(env);
      const verses = VERSES_BY_ENV[env];
      const all: VxSale[] = [];
      for (const v of verses) {
        const rows = await fetchAllSales({ token, verseId: v.verseId, gatewayId }, (p) =>
          setSalesProgress(p)
        );
        all.push(...rows);
      }
      setSales(all);
    } catch (e) {
      setSalesErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSalesLoading(false);
    }
  };

  const stageFiltered = useMemo(
    () => (env === 'prod' ? sales.filter((s) => s.stage === 'PROD') : sales),
    [sales, env]
  );
  const validSales = useMemo(() => stageFiltered.filter((s) => !s.refundedAt), [stageFiltered]);

  const buckets = useMemo(() => {
    const todayMs = localMidnightMs(0);
    const weekMs = localMidnightMs(6);
    const monthMs = localMidnightMs(29);
    const inRange = (fromMs: number) =>
      validSales.filter((s) => {
        const t = Date.parse(s.createdAt);
        return !Number.isNaN(t) && t >= fromMs;
      });
    const payerCount = (rows: VxSale[]) => new Set(rows.map((s) => s.buyer.uid)).size;
    const revenue = (rows: VxSale[]) => rows.reduce((sum, s) => sum + s.totalPrice / 100, 0);
    const today = inRange(todayMs);
    const week = inRange(weekMs);
    const month = inRange(monthMs);
    return {
      payers: { today: payerCount(today), week: payerCount(week), month: payerCount(month) },
      revenue: { today: revenue(today), week: revenue(week), month: revenue(month) },
    };
  }, [validSales]);

  const salesLoaded = sales.length > 0;

  return (
    <SupabaseLoginGate>
      <section className="flex flex-col gap-4">
        <header>
          <h2 className="text-2xl font-semibold tracking-tight">Dashboard</h2>
          <p className="text-sm text-muted-foreground">
            주요 KPI 한눈에 보기. 신규 가입자/접속자는 자동 갱신, 매출/과금자는 "불러오기" 버튼
            클릭. DAU/WAU/MAU 는 GA4 BigQuery (스트리밍) — 5분 캐시.
          </p>
        </header>

        <Card>
          <CardContent className="flex flex-wrap items-end gap-4 p-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Env</span>
              <Select value={env} onValueChange={(v) => setEnv(v as EnvChoice)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prod">prod</SelectItem>
                  <SelectItem value="staging">staging</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="button" onClick={handleLoadSales} disabled={salesLoading}>
              {salesLoading ? '매출 불러오는 중…' : '매출 데이터 불러오기'}
            </Button>
            {salesLoading && salesProgress && (
              <span className="text-sm text-muted-foreground">
                page {salesProgress.page} · {salesProgress.total.toLocaleString()}건
              </span>
            )}
          </CardContent>
        </Card>

        {signupErr && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">
              신규 가입자 페치 실패: {signupErr}
            </AlertDescription>
          </Alert>
        )}
        {salesErr && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">
              매출 페치 실패: {salesErr}
            </AlertDescription>
          </Alert>
        )}
        {activeErr && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">
              DAU/WAU/MAU 페치 실패: {activeErr}
            </AlertDescription>
          </Alert>
        )}
        {retentionErr && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">
              D7 리텐션 페치 실패: {retentionErr}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <KpiCard label="신규 가입자" highlight>
            <KpiRow tag="오늘" value={signup ? signup.today.toLocaleString() : '—'} />
            <KpiRow tag="7일" value={signup ? signup.thisWeek.toLocaleString() : '—'} />
            <KpiRow tag="30일" value={signup ? signup.thisMonth.toLocaleString() : '—'} />
          </KpiCard>

          <KpiCard label="과금자 수 (환불 제외)" highlight>
            <KpiRow tag="오늘" value={salesLoaded ? buckets.payers.today.toLocaleString() : '—'} />
            <KpiRow tag="7일" value={salesLoaded ? buckets.payers.week.toLocaleString() : '—'} />
            <KpiRow tag="30일" value={salesLoaded ? buckets.payers.month.toLocaleString() : '—'} />
          </KpiCard>

          <KpiCard label="과금금액 (순매출)" highlight>
            <KpiRow tag="오늘" value={salesLoaded ? usd(buckets.revenue.today) : '—'} />
            <KpiRow tag="7일" value={salesLoaded ? usd(buckets.revenue.week) : '—'} />
            <KpiRow tag="30일" value={salesLoaded ? usd(buckets.revenue.month) : '—'} />
          </KpiCard>

          <KpiCard label="금일 접속자 (DAU)" highlight>
            <KpiRow
              tag="Web2"
              value={active ? active.w2.dau.toLocaleString() : activeErr ? '에러' : '—'}
            />
            <KpiRow
              tag="Web3"
              value={active ? active.w3.dau.toLocaleString() : activeErr ? '에러' : '—'}
            />
            <KpiRow
              tag="합계"
              value={active ? active.total.dau.toLocaleString() : activeErr ? '에러' : '—'}
            />
          </KpiCard>
          <KpiCard label="금주 접속자 (WAU)" highlight>
            <KpiRow
              tag="Web2"
              value={active ? active.w2.wau.toLocaleString() : activeErr ? '에러' : '—'}
            />
            <KpiRow
              tag="Web3"
              value={active ? active.w3.wau.toLocaleString() : activeErr ? '에러' : '—'}
            />
            <KpiRow
              tag="합계"
              value={active ? active.total.wau.toLocaleString() : activeErr ? '에러' : '—'}
            />
          </KpiCard>
          <KpiCard label="금월 접속자 (MAU)" highlight>
            <KpiRow
              tag="Web2"
              value={active ? active.w2.mau.toLocaleString() : activeErr ? '에러' : '—'}
            />
            <KpiRow
              tag="Web3"
              value={active ? active.w3.mau.toLocaleString() : activeErr ? '에러' : '—'}
            />
            <KpiRow
              tag="합계"
              value={active ? active.total.mau.toLocaleString() : activeErr ? '에러' : '—'}
            />
          </KpiCard>
          <KpiCard label="D7 리텐션" highlight>
            <KpiRow
              tag="Web2 코호트"
              value={
                retention ? retention.w2.cohortSize.toLocaleString() : retentionErr ? '에러' : '—'
              }
            />
            <KpiRow
              tag="Web2 잔류"
              value={
                retention ? retention.w2.retained.toLocaleString() : retentionErr ? '에러' : '—'
              }
            />
            <KpiRow
              tag="Web2 비율"
              value={retention ? `${retention.w2.rate}%` : retentionErr ? '에러' : '—'}
            />
            <KpiRow
              tag="Web3 코호트"
              value={
                retention ? retention.w3.cohortSize.toLocaleString() : retentionErr ? '에러' : '—'
              }
            />
            <KpiRow
              tag="Web3 잔류"
              value={
                retention ? retention.w3.retained.toLocaleString() : retentionErr ? '에러' : '—'
              }
            />
            <KpiRow
              tag="Web3 비율"
              value={retention ? `${retention.w3.rate}%` : retentionErr ? '에러' : '—'}
            />
            <KpiRow
              tag="전체 코호트"
              value={
                retention
                  ? retention.total.cohortSize.toLocaleString()
                  : retentionErr
                    ? '에러'
                    : '—'
              }
            />
            <KpiRow
              tag="전체 잔류"
              value={
                retention ? retention.total.retained.toLocaleString() : retentionErr ? '에러' : '—'
              }
            />
            <KpiRow
              tag="전체 비율"
              value={retention ? `${retention.total.rate}%` : retentionErr ? '에러' : '—'}
            />
          </KpiCard>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Link
            to="/funnel"
            className="flex flex-col gap-1 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
          >
            <span className="text-sm font-semibold">
              스테이지 진행 퍼널 <span aria-hidden="true">→</span>
            </span>
            <span className="text-xs text-muted-foreground">
              가입 → 출석 1회+ → Stage 1/10/20/30/40/50+ 클리어 전환율
            </span>
          </Link>
          <Link
            to="/shop/revenue"
            className="flex flex-col gap-1 rounded-lg border bg-card p-4 transition-colors hover:bg-accent"
          >
            <span className="text-sm font-semibold">
              결제 매출 상세 <span aria-hidden="true">→</span>
            </span>
            <span className="text-xs text-muted-foreground">
              VX 매출 대시보드 — 일별/상품별/과금 상위 50 (verse·기간 필터)
            </span>
          </Link>
        </div>

        <AttendanceDistributionPanel />

        <p className="text-xs text-muted-foreground">
          매출/과금자 source: VX sales API (env: <b>{env}</b>). 신규 가입자 source: V8{' '}
          <code>users</code> 컬렉션 createdAt 필터. DAU/WAU/MAU·D7 리텐션은 GA4 app_env=prod-w2/w3
          로 분리(로그인 user_id 기준). D7 리텐션: 첫 활동일이 7일 전인 유저 중 오늘 재활동 비율.
        </p>
      </section>
    </SupabaseLoginGate>
  );
}

function KpiCard({
  label,
  children,
  highlight,
  placeholder,
}: {
  label: string;
  children?: React.ReactNode;
  highlight?: boolean;
  placeholder?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-4">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {placeholder ? (
          <>
            <span className="text-2xl font-semibold tabular-nums text-muted-foreground/40">—</span>
            <span className="text-[10px] text-muted-foreground">
              데이터 적재 중 (GA4 BQ 24h 후)
            </span>
          </>
        ) : (
          <div
            className={['flex', 'flex-col', 'gap-1', highlight ? 'text-primary' : '']
              .filter(Boolean)
              .join(' ')}
          >
            {children}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function KpiRow({ tag, value }: { tag: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-xs text-muted-foreground">{tag}</span>
      <span className="text-xl font-semibold tabular-nums">{value}</span>
    </div>
  );
}
